import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { AsyncPipe, NgIf } from '@angular/common';
import { BaseComponent } from '../../../../models/base/base-component';
import { LookAheadItem } from '../search-with-look-ahead/look-ahead-list/look-ahead-item/protocol/look-ahead-item';
import { OdataTypeaheadSearchViewModel } from './odata-typeahead-search-view-model';
import { PopperUtils } from '../../../../utils/popper-utils';
import {
  NgxPopperjsContentComponent,
  NgxPopperjsModule,
  NgxPopperjsPlacements,
  NgxPopperjsTriggers
} from 'ngx-popperjs';
import { takeUntil } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { LookAheadListComponent } from '../search-with-look-ahead/look-ahead-list/look-ahead-list.component';
import { LoadingComponent } from '../loading/loading.component';

@Component({
  selector: 'app-odata-typeahead-search',
  standalone: true,
  imports: [NgIf, AsyncPipe, NgxPopperjsModule, LoadingComponent, LookAheadListComponent],
  templateUrl: './odata-typeahead-search.component.html',
  styleUrls: ['./odata-typeahead-search.component.scss'],
  providers: [OdataTypeaheadSearchViewModel]
})
export class OdataTypeaheadSearchComponent extends BaseComponent implements OnChanges {
  @Input() disablePopper: boolean = false;
  @Input() placeHolder: string = 'Search';
  @Input() lookAheadItems: LookAheadItem[] = [];
  @Input() label: string = '';
  @Input() controlId = 'searchBar';
  @Input() searchedProperties!: string[];
  @Input() useProfileListItems: boolean = false;
  @Input() displayValueInline: boolean = false;
  @Input() disabled: boolean = false;
  @Input() odataFunction!: (searchString: string) => Observable<LookAheadItem[]>;
  @Input() hasErrors: boolean = false;
  @Input() popperClass: string = 'look-ahead-popper';
  @Input() customItemTemplate: TemplateRef<any> | undefined;
  @Output() searchText = new EventEmitter(true);
  @Output() lookAheadSelected = new EventEmitter<any>(true);
  @Output() lookAheadCleared = new EventEmitter<any>(true);
  @ViewChild('popperContent') popperContent!: NgxPopperjsContentComponent;
  @ViewChild('searchBar') searchBar!: ElementRef<HTMLInputElement>;

  private viewModel = inject(OdataTypeaheadSearchViewModel);

  public isLoading$ = this.viewModel.isLoading$;
  public loadingOptions$ = this.viewModel.loadingOpts$;

  public selectedItem: LookAheadItem | null = null;

  private pendingSelection: LookAheadItem | null = null;

  /**
   * A selection made before this component existed, so a search that is recreated — a wizard step
   * revisited, a branch re-rendered — shows the choice its form still holds rather than an empty box.
   */
  @Input() set initialSelection(item: LookAheadItem | null) {
    this.pendingSelection = item;
    this.showPendingSelection();
  }

  setupBindings(): void {
    this.searchText.pipe(takeUntil(this.onDestroy)).subscribe(text => this.viewModel.connectToSearchText(text));

    this.viewModel.lookAheadHits$.pipe(takeUntil(this.onDestroy)).subscribe(hits => {
      hits?.length > 0 && !this.disablePopper ? this.popperContent.show() : this.popperContent.hide();
      this.lookAheadItems = hits;
    });

    this.viewModel.isLoading$.pipe(takeUntil(this.onDestroy)).subscribe(isLoading => {
      if (isLoading) this.popperContent.show();
    });

    this.showPendingSelection();
  }

  /** Waits for the input to exist, since the chosen value is written onto it directly. */
  private showPendingSelection(): void {
    const item = this.pendingSelection;
    if (!item || !this.displayValueInline || !item.lookAheadDisplayValue || !this.searchBar) return;
    this.selectedItem = item;
    this.searchBar.nativeElement.value = item.lookAheadDisplayValue();
  }

  setupViews(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.odataFunction) this.viewModel.connectToOdataFunction(this.odataFunction);
  }

  public searchChanged(e: Event) {
    const target = e.target as HTMLInputElement;
    this.searchText.emit(target.value);
  }

  public clearSearch() {
    this.searchText.emit('');
    this.searchBar.nativeElement.value = '';
  }

  public handleClickOutside() {
    this.popperContent.hide();
  }

  public itemSelected(item: LookAheadItem) {
    this.lookAheadSelected.emit(item);
    this.clearSearch();
    this.popperContent.hide();
    if (this.displayValueInline && item.lookAheadDisplayValue) {
      this.selectedItem = item;
      this.searchBar.nativeElement.value = item.lookAheadDisplayValue();
    }
  }

  public clearSelectedItem(): void {
    this.selectedItem = null;
    this.searchBar.nativeElement.value = '';
    this.lookAheadCleared.emit();
  }

  public popperModifier = [PopperUtils.flipModifier(['bottom', 'top']), PopperUtils.offsetModifier([0, 8])];
  public popperStyles = {
    'background-color': '#FFFFFF',
    'z-index': 99
  };
  protected readonly NgxPopperjsTriggers = NgxPopperjsTriggers;
  protected readonly NgxPopperjsPlacements = NgxPopperjsPlacements;
}
